import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { Button } from 'shadcn/components/ui/Button';
import { Dialog, DialogContent, DialogTitle } from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

import { PrintAllocatedLabelsModal } from './PrintAllocatedLabelsModal';
import { AllocationBreakdown } from './AllocationBreakdown/AllocationBreakdown';
import { PalletBuilderCard } from './PalletBuilder/PalletBuilderCard';
import { palletStagingReducer, sanitizeNumPalletsInput } from './PalletBuilder/palletBuilderUtils';
import { PalletQuickFillCard } from './PalletBuilder/PalletQuickFillCard';
import { ReceiveFooter } from './ReceiveFooter/ReceiveFooter';
import { summarizeEnteredCasesBySource } from './ReceiveFooter/receiveFooterUtils';

function getHeaderText(rowData, poType) {
    if (poType === 'PACKAGING') {
        return `Sku ${rowData.original.masterSku} - ${rowData.original.description} (${rowData.original.type})`;
    }
    return `Sku ${rowData.original.itemMasterSKU} - ${rowData.original.itemMasterName}`;
}

/** TODO: Replace with rowData.original.suggestedAllocations once the BAMS receiving API is available */
const MOCK_SUGGESTED_ALLOCATIONS = [
    {
        companySource: { id: 1, code: 'misfits_market', displayName: 'Misfits Market' },
        numCases: 139,
    },
    {
        companySource: { id: 2, code: 'blue_apron', displayName: 'Blue Apron' },
        numCases: 98,
    },
    {
        companySource: { id: 3, code: 'marley_spoon', displayName: 'Marley Spoon' },
        numCases: 87,
    },
];

function handleReceiveZeroPallets() {
    // TODO: Implement receive 0 pallets action
}

function getLineItemKey(rowData) {
    if (!rowData?.original) {
        return null;
    }

    const { id, itemMasterSKU, masterSku, poMapID } = rowData.original;
    // Grocery lines use `id` as the PO map id; packaging lines use `poMapID`.
    return poMapID ?? id ?? itemMasterSKU ?? masterSku ?? null;
}

function ReceiveAllocatedLineItemModalBody({ companySourceOptions, isFbmMode, onSubmitSuccess, suggestedAllocations }) {
    const [numPallets, setNumPallets] = useState('');
    const [pallets, dispatch] = useReducer(palletStagingReducer, []);
    const resolvedNumPallets = Math.max(0, Math.floor(Number(numPallets) || 0));
    const showPalletBuilder = resolvedNumPallets > 0;

    const handleNumPalletsChange = useCallback(
        (event) => {
            const sanitized = sanitizeNumPalletsInput(event.target.value);
            setNumPallets(sanitized);
            dispatch({
                type: 'SYNC_NUM_PALLETS',
                numPallets: Math.max(0, Math.floor(Number(sanitized) || 0)),
                companySourceOptions,
                isFbmMode,
            });
        },
        [companySourceOptions, isFbmMode],
    );

    const handleApplyToAllPallets = useCallback(
        (values) => {
            dispatch({ type: 'APPLY_QUICK_FILL', values, isFbmMode });
        },
        [isFbmMode],
    );

    const handleSubmitAndPrintLabels = useCallback(() => {
        const { grandTotal } = summarizeEnteredCasesBySource(pallets, companySourceOptions);

        // TODO: Replace mock success with BAMS receiving API call
        onSubmitSuccess({
            totalCases: grandTotal,
            numPallets: resolvedNumPallets,
        });
    }, [companySourceOptions, onSubmitSuccess, pallets, resolvedNumPallets]);

    return (
        <>
            <AllocationBreakdown allocations={suggestedAllocations} />
            <div className="mt-4 flex items-end justify-between gap-6">
                <div className="w-40">
                    <Label htmlFor="num-pallets">Num Pallets</Label>
                    <Input
                        id="num-pallets"
                        className="mt-2"
                        min={0}
                        step={1}
                        type="number"
                        value={numPallets}
                        onChange={handleNumPalletsChange}
                    />
                </div>
                <Button type="button" variant="outline" onClick={handleReceiveZeroPallets}>
                    Receive 0 Pallets
                </Button>
            </div>
            {showPalletBuilder ? (
                <>
                    <PalletQuickFillCard
                        className="mt-4"
                        companySourceOptions={companySourceOptions}
                        isFbmMode={isFbmMode}
                        onApplyToAllPallets={handleApplyToAllPallets}
                    />
                    <div className="mt-3 flex max-h-[min(50vh,28rem)] flex-col gap-2 overflow-y-auto">
                        {pallets.map((pallet, index) => (
                            <PalletBuilderCard
                                key={pallet.id}
                                companySourceOptions={companySourceOptions}
                                isFbmMode={isFbmMode}
                                pallet={pallet}
                                palletIndex={index}
                                onAddLine={() =>
                                    dispatch({
                                        type: 'ADD_LINE',
                                        palletId: pallet.id,
                                        companySourceOptions,
                                        isFbmMode,
                                    })
                                }
                                onRemoveLine={(lineId) =>
                                    dispatch({ type: 'REMOVE_LINE', palletId: pallet.id, lineId })
                                }
                                onUpdateLine={(lineId, patch) =>
                                    dispatch({ type: 'UPDATE_LINE', palletId: pallet.id, lineId, patch })
                                }
                            />
                        ))}
                    </div>
                    <ReceiveFooter
                        companySourceOptions={companySourceOptions}
                        isFbmMode={isFbmMode}
                        pallets={pallets}
                        onSubmit={handleSubmitAndPrintLabels}
                    />
                </>
            ) : null}
        </>
    );
}

function ReceiveAllocatedLineItemModal({ close, isFbmMode = false, open, poType, rowData }) {
    const lineItemKey = getLineItemKey(rowData);
    const [showPrintLabels, setShowPrintLabels] = useState(false);
    const [receivedSummary, setReceivedSummary] = useState(null);

    const handleOpenChange = useCallback(
        (isOpen) => {
            if (!isOpen) {
                setShowPrintLabels(false);
                setReceivedSummary(null);
                close();
            }
        },
        [close],
    );

    const handleSubmitSuccess = useCallback((summary) => {
        setReceivedSummary(summary);
        setShowPrintLabels(true);
    }, []);

    useEffect(() => {
        if (!open) {
            setShowPrintLabels(false);
            setReceivedSummary(null);
        }
    }, [open]);

    const suggestedAllocations = MOCK_SUGGESTED_ALLOCATIONS;

    const companySourceOptions = useMemo(() => {
        const seen = new Set();
        return suggestedAllocations
            .map((allocation) => allocation.companySource)
            .filter((source) => {
                if (seen.has(source.id)) {
                    return false;
                }
                seen.add(source.id);
                return true;
            });
    }, [suggestedAllocations]);

    if (!rowData) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent aria-describedby={undefined} className="gap-0 overflow-hidden p-0 sm:max-w-4xl">
                {showPrintLabels && receivedSummary ? (
                    <PrintAllocatedLabelsModal poType={poType} receivedSummary={receivedSummary} rowData={rowData} />
                ) : (
                    <>
                        <div className="rounded-t-lg border-2 border-[#4C884B] bg-[#B0C472] px-6 py-4">
                            <DialogTitle className="p-0 text-base font-semibold">
                                {getHeaderText(rowData, poType)}
                            </DialogTitle>
                        </div>
                        <div className="p-6">
                            <ReceiveAllocatedLineItemModalBody
                                key={`${lineItemKey}-${open}`}
                                companySourceOptions={companySourceOptions}
                                isFbmMode={isFbmMode}
                                suggestedAllocations={suggestedAllocations}
                                onSubmitSuccess={handleSubmitSuccess}
                            />
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

export {
    ReceiveAllocatedLineItemModal,
    getHeaderText,
    getLineItemKey,
    handleReceiveZeroPallets,
    MOCK_SUGGESTED_ALLOCATIONS,
};
